import CartContent from './cart-content.js?v=5';
import { E as EVENTS } from './events-58bc9098.js';
import './modals-452e00fa.js';
import { i as insertedMarkupFixes } from './inserted-markup-fixes-26b497bf.js'

class CartDrawer extends CartContent {
  constructor() {
    super();
    this.latestCartDataRefreshId = 0;
    this.cartDataChangePending = false;

    this.classes = {
      ...this.classes,
      freeShippingBarIntialState: "free-shipping-bar--force-progress-to-0",
    };

    this.selectors = {
      ...this.selectors,
      templateTag: "template[data-template-key='cart-drawer']",
      dialogParent: "dialog",
      cartDrawer: "cart-drawer",
      drawerInner: "drawer-inner",
      footer: "[js-drawer-footer]",
      scrollableViewport: ".drawer-viewport",
      inlineSubtotal: "[js-inline-subtotal]",
      keepShoppingButton: "[js-keep-shopping-button]",
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.dialog = this.closest(this.selectors.dialogParent);
    this.eventOptions = { signal: this.controller.signal }
    this.cartStatusTemplate = this.querySelector('template[data-cart-status]')?.content
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-refresh-retry]')) {
        this.showCartStatus()
        this.refreshCartData()
      }
    }, this.eventOptions)

    // A cart mutation must invalidate any earlier drawer read.
    document.addEventListener(EVENTS.CART_ITEM_CHANGE_PENDING, () => {
      this.latestCartDataRefreshId += 1;
      this.cartDataChangePending = true;
    }, { signal: this.controller.signal });

    document.addEventListener(EVENTS.CART_CHANGED, () => {
      this.latestCartDataRefreshId += 1
      this.cartDataChangePending = false
    }, this.eventOptions)
    document.addEventListener(EVENTS.CART_ERROR, () => {
      this.cartDataChangePending = false
      if (this.querySelector('[data-cart-refresh-status]')) {
        this.showCartStatus(true)
      }
    }, this.eventOptions)

    window.addEventListener('pageshow', (event) => {
      this.refreshCartOnPageRestore(event);
    }, { signal: this.controller.signal });
    // The element may be upgraded after pageshow on a full-document Back visit.
    this.refreshCartOnPageRestore()

    // Remove free gift items from cart drawer on load
    this.removeFreeGiftItems();

    setTimeout(() => {
      // There is a Shopify quirk where images via storefront rendering use "shop.myshopify.com/cdn", but
      // images via section rendering use "cdn.shopify.com". This leads to visual glitches when
      // "#refetchContents()" runs and refreshes the cart (while cart drawer is open).
      //
      // To mitigate this, we can refetch preemptively when the cart is closed so that any
      // future updates have reduced glitches because the initial markup is already swapped.
      //
      // Other have noted this in the Partners slack:
      //   https://shopifypartners.slack.com/archives/C4E704GBX/p1712156095481229
      if (!this.hasDoneInitialRefetch && !this.dialog.open) {
        super.refetchContents();
      }
    }, 3000);

    if (window?.Shopify?.designMode) {
      this.#bindThemeEditorEvents();
    }

    window.addEventListener(EVENTS.CART_DRAWER_OPENING, () => {
      // The lazy template may contain a cached, empty cart after checkout.
      // Section rendering supplies current contents before opening animations.
      if (!this.querySelector(this.selectors.scrollableViewport)) {
        this.showCartStatus()
      }
      this.refreshCartData().then((refreshed) => {
        if (!refreshed || this.cartDataChangePending) {
          return
        }
        const freeShippingBar = this.querySelector(this.selectors.freeShippingBar)
        if (freeShippingBar) {
          freeShippingBar.classList.add(this.classes.freeShippingBarIntialState)
          setTimeout(() => {
            freeShippingBar.classList.remove(this.classes.freeShippingBarIntialState)
          }, 600)
        }
      })
    }, this.eventOptions)

    document.addEventListener(EVENTS.CART_ITEM_ADDED, () => {
      this.scrollableViewport?.scrollTo({
        top: 0,
        behavior: this.dialog.open ? "smooth" : "instant",
      });
    }, this.eventOptions)
  }

  getHTMLFromSectionResponseText(responseText) {
    // Overide: the drawer features a template, we have to override
    // this function from the super class

    const responseHTML = document
      .createRange()
      .createContextualFragment(responseText);

    const templateContent = responseHTML.querySelector(
      this.selectors.templateTag,
    ).innerHTML;

    const templateContentHTML = document
      .createRange()
      .createContextualFragment(templateContent);

    insertedMarkupFixes(templateContentHTML)

    return templateContentHTML
  }

  /**
   * Refresh cached and full-document returns, even after a late upgrade.
   * @param {PageTransitionEvent} [event] - Browser page restoration event.
   * @returns {Promise<boolean>|undefined} Cart refresh, when restored.
   */
  refreshCartOnPageRestore(event) {
    const navigation = window.performance?.getEntriesByType('navigation')[0]
    if (!event?.persisted && navigation?.type !== 'back_forward') {
      return
    }
    this.showCartStatus()
    return this.refreshCartData()
  }

  /**
   * Keep closing and retrying available without showing stale cart lines.
   * @param {boolean} failed - Whether the current refresh failed.
   * @returns {void}
   */
  showCartStatus(failed = false) {
    if (!this.cartStatusTemplate) {
      return
    }
    this.replaceChildren(this.cartStatusTemplate.cloneNode(true))
    this.setAttribute('aria-busy', String(!failed))
    if (failed) {
      this.querySelector('[data-cart-refresh-status]').textContent = this.dataset.cartRefreshError
      this.querySelector('[data-cart-refresh-retry]').hidden = false
    }
  }

  /**
   * Sync the drawer with Shopify, including cached checkout returns.
   * @returns {Promise<boolean>} Whether current drawer markup was rendered.
   */
  refreshCartData() {
    const refreshId = ++this.latestCartDataRefreshId
    // A newer JSON read also supersedes section HTML from an older read.
    this.latestRefreshId += 1
    const cartPath = window.theme?.routes?.cart?.base || '/cart'
    return fetch(`${cartPath}.js`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cart refresh failed (${response.status})`)
        }
        return response.json()
      })
      .then(async (cartData) => {
        if (
          refreshId !== this.latestCartDataRefreshId ||
          this.cartDataChangePending
        ) {
          return false
        }

        document.dispatchEvent(new CustomEvent(EVENTS.CART_CHANGED, {
          detail: { cart: cartData, cartData },
        }))
        const acceptedId = this.latestCartDataRefreshId
        // Cart events also fetch section HTML. Wait for that work, including
        // any newer refresh dispatched by orphaned-gift cleanup.
        let pending
        let refreshed
        do {
          pending = this.cartRefreshPromise
          refreshed = await pending
        } while (pending !== this.cartRefreshPromise)
        if (acceptedId !== this.latestCartDataRefreshId || this.cartDataChangePending) {
          return false
        }
        if (!refreshed && !this.cartDataChangePending) {
          this.showCartStatus(true)
        }
        return Boolean(refreshed && !this.cartDataChangePending)
      })
      .catch((error) => {
        if (
          refreshId !== this.latestCartDataRefreshId ||
          this.cartDataChangePending
        ) {
          return false
        }

        document.dispatchEvent(new CustomEvent(EVENTS.CART_ERROR, {
          detail: { errorMessage: error.message },
        }))
        this.showCartStatus(true)
        return false
      })
  }

  swapFullContents(newHTML) {
    this.replaceChildren(...newHTML.children);
  }

  determineRefreshMode(newHTML) {
    if (!this.dialog.open) {
      return this.REFRESH_MODES.FULL;
    }

    return super.determineRefreshMode(newHTML);
  }

  contentsRefetched() {
    this.setAttribute('aria-busy', 'false')
    this.setScrollableViewport();
    this.#bindElementEvents();
  }

  #handleCartTextIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting !== this.cartTextIsVisible) {
        this.cartTextIsVisible = entry.isIntersecting;
        this.dataset.cartTextIsVisible = entry.isIntersecting;
      }
    });
  }

  #bindElementEvents() {
    // TODO: cart-note.js should scroll textarea into view but it
    // is not working reliably, so scrolling to bottom here for now
    this.addEventListener(
      EVENTS.CART_NOTE_OPENED,
      () => {
        if (this.scrollableViewport) {
          this.scrollableViewport.scrollTop =
            this.scrollableViewport.scrollHeight;
        }
      },
      {
        signal: this.controller.signal,
      },
    );

    this.keepShoppingButton = this.querySelector(
      this.selectors.keepShoppingButton,
    );

    this.keepShoppingButton?.addEventListener(
      "click",
      () => {
        window.dispatchEvent(new Event(EVENTS.CART_DRAWER_CLOSE));
      },
      {
        signal: this.controller.signal,
      },
    );

    const inlineSubtotal = this.querySelector(this.selectors.inlineSubtotal);

    if (this.inlineSubtotalIntersectionObserver) {
      this.inlineSubtotalIntersectionObserver.disconnect();
      this.inlineSubtotalIntersectionObserver = null;
    }

    if (inlineSubtotal) {
      this.inlineSubtotalIntersectionObserver = new IntersectionObserver(
        this.#handleCartTextIntersection.bind(this),
        {
          root: this.scrollableViewport,
          rootMargin: "0px 0px -95px 0px",
          threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        },
      );

      this.inlineSubtotalIntersectionObserver.observe(inlineSubtotal);
    }
  }

  #bindThemeEditorEvents() {
    document.addEventListener(
      EVENTS.SHOPIFY_SECTION_SELECT,
      (event) => {
        if (event.target === this.shopifySection) {
          window.dispatchEvent(new Event(EVENTS.CART_DRAWER_OPEN));
        }
      },
      this.eventOptions,
    );

    document.addEventListener(
      EVENTS.SHOPIFY_SECTION_DESELECT,
      (event) => {
        if (event.target === this.shopifySection) {
          window.dispatchEvent(new Event(EVENTS.CART_DRAWER_CLOSE));
          this.openedViaThemeEditorEvents = false;
        }
      },
      this.eventOptions,
    );
  }


  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}
